import * as _ from "lodash";
import {SensorConfigCollection} from "../collections/SensorConfigs";
import {ChannelRecord, Module} from "../models/Module";

var BitArray = require("../../../common/bitArray.js");
var crc = require('crc');

export interface SensorStatusConfig {
    sensorConfigCollection: SensorConfigCollection;
    moduleModel: Module;
}

//shortDescription shows on modules on the graph
//pass timestamp for a right pushed timestamp next to the sensor error
//pass no description for only notifying the status, with no code or description

//Any error with no description will be ignored
export class SensorStatus {
    constructor(private options: SensorStatusConfig) {
        this.options = options;
        };

    _getInternalSensorStatus() {
        var con = this.options.moduleModel.getConnector(0);
        if(!con){
            return {};
        }
        var errorT = this.options.moduleModel.getChannelValue(0, 38, 0);
        var errorP = this.options.moduleModel.getChannelValue(0, 39, 0);
        var errorV = this.options.moduleModel.getChannelValue(0, 40, 0);

        var errors = [];

        var bat  = this.options.moduleModel.getBatteryV();
        var hum  = this.options.moduleModel.getHumidityPct();
        var temp = this.options.moduleModel.getTemperatureF();

        var lowBat   = (bat !== false && bat > 0 && bat <= 3.6);
        var highHum  = (hum !== false && hum >= 75);
        var highTemp = (temp !== false && temp >= 125);
        var inUse    = this.options.moduleModel.connectorInUse(0);
        var distance = this.options.moduleModel.getChannelValue(0, 4, 0);
        //If any module errors

        //Dead Battery and Check Module must be first to be tested,
        //It makes them the primary error in later functions
        if (!inUse && lowBat) {
            errors.push({
                // the absence of a description hides this warning
                // description: "Dead Battery",
                status: "danger",
                overrideMessage: "Dead Battery",
                shortDescription: "Dead Battery",

            });
        } else if (!inUse){
            errors.push({
                // the absence of a description hides this warning
                // description: "Check Module",
                shortDescription: "Offline",
                status: "danger",
                overrideMessage: "Offline"
            });
        } else if (this.options.moduleModel.moduleIsUploadingData()) {
            var percentage = this.options.moduleModel.getChannelValue(0, 48, false);
            var percentageStr;
            if(percentage === false) {
                percentageStr = ' Data'
            } else {
                percentage = Math.max(0, Math.min(100, percentage));
                percentageStr = ' (' + percentage + '%)'
            }

            errors.push({
                shortDescription: "Uploading" + percentageStr,
                overrideMessage: "Uploading" + percentageStr,
                status: "success",
            });
        }
        if (inUse && lowBat) {
            errors.push({
                description: "Change Battery",
                status: "warning",
            });
        }
        if (highTemp) {
            errors.push({
                shortDescription: "Check Temp",
                description: "Temperature too high",
                status: "warning",
            });
        }
        if (highHum) {
            errors.push({
                shortDescription: "Check Humidity",
                description: "Humidity too high",
                status: "warning",
            });
        }

        if (distance == 255){
            errors.push({
                description: "The module has trouble connecting to it's nearest neighbor",
                causes: ["Consider adding a relay node"],
                status: "warning"
            });
        }
        //Error codes for modules' internal
        switch(errorT){
            case 2:
                errors.push({
                    description: "Unexpected Reset",
                    status: "warning",
                    showCode: false,
                    causes: ["Node reset unexpectedly and cleared its local configuration"],
                });
                break;
            case 3:
                if(distance != 255) {
                    errors.push({
                        description: "Node performed discovery for a neighbor",
                        status: "success",
                        showCode: false,
                        causes: ["Node had trouble connecting to a neighbor or the Gateway; consider adding a relay Node"],
                    });
                }
                break;
            case 4:
                errors.push({
                    description: "Error polling for data",
                    status: "success",
                    showCode: true,
                    causes: ["Node had invalid next poll time, likely due to high sampling frequency"],
                });
                break;
            case 200:
                if(errorP >= 0 && errorP < 256){
                    errors.push({
                        description: "Error reading internal sensor",
                        status: "warning",
                    });
                }
                break;
            case 201:
                if(errorP == 0 || errorP == 1 || errorP == 2 || errorP == 3){
                    errors.push({
                        description: "CRC error on connector 1",
                        status: "success",
                        showCode: true,
                    });
                }
                break;
            case 202:
                if(errorP == 0 || errorP == 1 || errorP == 2 || errorP == 3){
                    errors.push({
                        description: "CRC error on connector 2",
                        status: "success",
                        showCode: true,
                    });
                }
                break;
            case 206:
                if(errorP == 0){
                    errors.push({
                        description: "Transmission Error",
                        status: "success",
                        showCode: true,
                        causes: ["Neighbor did not respond to packet transmission; if error persists consider adding a relay Node"],
                    });
                }else if(errorP == 1){
                    errors.push({
                        description: "Transmission Error",
                        status: "warning",
                        showCode: true,
                        causes: ["Node discarded packet due to neighbor not responding; if error persists consider adding a relay Node"],
                    });
                }
                break;
            case 207:
                errors.push({
                    description: "Radio Buffer Overrun",
                    status: "warning",
                    showCode: true,
                    causes: ["Gateway received too many packets at one time; if time sync is enabled, consider disabling"],
                });
                break;
            case 203:
                if(errorP == 0 || errorP == 1 || errorP == 2 || errorP == 3){
                    errors.push({
                        description: "CRC error on connector 3",
                        status: "success",
                        showCode: true,
                    });
                }
                break;
            case 232:
                errors.push({
                    status: "success",
                });
                break;
        }
        //General errors for all connectors
        switch(errorT){
            case 204:
                errors.push({
                    description: "Error applying Update",
                    status: "warning",
                });
                break;
            case 205:
                errors.push({
                    description: "Error applying Action",
                    status: "warning",
                });
                break;
            case 230:
                errors.push({
                    description: "Action executed",
                    status: "success",
                    showCode: true,
                });
                break;
            case 231:
                errors.push({
                    description: "Update applied",
                    status: "success",
                    showCode: true,
                });
                break;
            case 233:
                errors.push({
                    status: "success",
                });
                break;
        }
        //If no short description defined, set as "Module Error"
        _.each(errors, (error) => {
            if(status && status != "success"
                && error['description']
                && !error['shortDescription'])
            {
                error['shortDescription'] = "Module Error";
            }
        });
        return {
            errors: errors,
            errorT: errorT,
            errorP: errorP,
            errorV: errorV,
        };
    }

    _getAttachedSensorStatus(connector: number) {
        var con = this.options.moduleModel.getConnector(connector);
        if(!con){
            return {};
        }
        var errorT = this.options.moduleModel.getChannelValue(0, 11 + (connector*8), 0);
        var errorP = this.options.moduleModel.getChannelValue(0, 12 + (connector*8), 0);
        var errorV = this.options.moduleModel.getChannelValue(0, 13 + (connector*8), 0);
        var errors = [];
        var sensorType = con["sensor_type"];
        var numChannels = con['channels'].length;
        var AAA = this.options.moduleModel.isBatteryPowered();

        //Sensor statuses
        //These are statuses that only can occur to sensor connectors (not the internal sensor)
        switch(sensorType){
            case 22:
            case 23:
                if(AAA){
                    errors.push({
                        description: "Modbus sensor bridges can only be connected to non-AAA powered Nodes",
                        status: "warning"
                    });
                }
                if (numChannels <= 0){
                    errors.push({
                        description: "Modbus sensor is working properly but is not yet configured to read any data.",
                        status: "warning",
                        causes: ["Set up Modbus Registers to read in the Module Configuration Tab"]
                    });
                }
                break;
            case 27:
                if(AAA){
                    errors.push({
                        description: "BACnet sensor bridges can only be connected to non-AAA powered Nodes",
                        status: "warning"
                    });
                }
                if (numChannels <= 0){
                    errors.push({
                        description: "BACnet sensor is working properly but is not yet configured to read any data.",
                        status: "warning"
                    });
                }
                break;
            case 30:
                if(AAA){
                    // Positions documented are offset by 2
                    if(this.options.moduleModel.getChannelValue(connector, 5-2, null)){
                        errors.push({
                            description: "CO2 enabled Environmental sensor bridges can only be connected to non-AAA powered Nodes",
                            status: "warning",
                        });
                    } else if (_.find(<ChannelRecord[]> con.channels, (channel: any) => {
                            return channel['channel_position'] >= (8-2) && channel['channel_position'] <= (19-2) && channel['channel_converted_value'];
                        })){
                            errors.push({
                                description: "PM enabled Environmental sensor bridges can only be connected to non-AAA powered Nodes",
                                status: "warning",
                            });
                    }
                }
                break;
        }
        //Sensor specific statuses
        //These statuses where error codes mean different things depending on sensor type
        switch(sensorType){
            // Temp/Hum sensor
            case 17:
                switch(errorT){
                    case 1:
                        switch(errorP){
                            case 0:
                            case 1:
                            case 2:
                                errors.push({
                                    description: "Sensor read zero",
                                    status: "warning",
                                    showCode: true,
                                });
                                break;
                        }
                        break;
                    case 2:
                        switch(errorP){
                            case 0:
                            case 1:
                            case 2:
                                errors.push({
                                    description: "Error reading sensor",
                                    status: "warning",
                                    showCode: true,
                                });
                                break;
                        }
                        break;
                }
                break;
            //Modbus
            case 22:
            case 23:
                switch(errorT){
                    case 1:
                        errors.push({
                            description: "Invalid function code",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 2:
                        errors.push({
                            description: "Invalid data address or register",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 3:
                        errors.push({
                            description: "Invalid data value",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 4:
                    case 5:
                    case 6:
                        errors.push({
                            description: "Error executing function command",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 7:
                        errors.push({
                            description: "Timeout error",
                            status: "warning",
                            causes: [
                                "Swapped wires", "No device with specified address",
                                "Timeout duration too short"
                            ],
                            showCode: true,
                        });
                        break;
                    case 8:
                        errors.push({
                            description: "Timing error",
                            status: "warning",
                            causes: ["Timeout delay too short", "Sampling frequency too often"],
                        });
                        break;
                    case 9:
                        errors.push({
                            description: "Configuration error",
                            status: "warning",
                            causes: [
                                "Timeout delay too short",
                                "Sampling frequency too often"
                            ],
                        });
                        break;
                    case 10:
                        errors.push({
                            description: "Modbus error",
                            status: "warning"
                        });
                        break;
                    case 11:
                    case 12:
                        errors.push({
                            description: "Bridge error",
                            showCode: true,
                            status: "warning"
                        });
                        break;
                    case 13:
                        errors.push({
                            description: "Invalid response",
                            showCode: true,
                            status: "warning"
                        });
                        break
                }
                break;
            //BACNet
            case 27:
                switch(errorT){
                    case 1:
                        if(errorV == 0){
                            errors.push({
                                description: "Timeout error",
                                showCode: true,
                                status: "warning",
                                causes: [
                                    "Swapped wires", "No device with the specified address",
                                    "Timeout duration too short"
                                ],
                            });
                        }
                        break;
                    case 2:
                        if(errorV == 0){
                            errors.push({
                                description: "Invalid response",
                                showCode: true,
                                status: "warning",
                            });
                        }
                        break;
                    case 132:
                    case 4:
                        errors.push({
                            description: "Master timeout error",
                            status: "warning",
                            showCode: true,
                            causes: ["Wrong baud rate"]
                        });
                        break;
                    case 133:
                    case 5:
                        errors.push({
                            description: "Line hold error",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 131:
                    case 3:
                        errors.push({
                            description: "Data buffer mismatch",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 138:
                    case 10:
                        errors.push({
                            description: "BACnet Error Response",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 139:
                    case 11:
                        errors.push({
                            description: "BACnet Reject Response",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 140:
                    case 12:
                        errors.push({
                            description: "BACnet Abort Response",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 141:
                    case 13:
                        errors.push({
                            description: "Unknown BACnet Response",
                            showCode: true,
                            status: "warning",
                        });
                        break;
                    case 128:
                        errors.push({
                            description: "Auto Discovery in Progress",
                            status: "success",
                        });
                        break;
                    case 230:
                        if(errorV == 100){
                            errors.push({
                                description: "Action received (Discovery in Progress)",
                                status: "success",
                            });
                        }
                        break;
                }
                break;
            //Environmental sensor
            case 30:
                switch(errorT){
                    case 1:
                        switch(errorP){
                            case 1:
                                errors.push({
                                    description: "Temperature sensor error",
                                    status: "warning",
                                });
                                break;
                            case 2:
                                errors.push({
                                    description: "Humidity sensor error",
                                    status: "warning",
                                });
                                break;
                        }
                        break;
                    case 2:
                        switch(errorP){
                            case 1:
                                errors.push({
                                    description: "VOC sensor error",
                                    showCode: true,
                                    status: "warning",
                                });
                                break;
                            case 2:
                                errors.push({
                                    description: "VOC sensor initializing",
                                    status: "success",
                                });
                                break;
                            case 3:
                                errors.push({
                                    description: "No new VOC data",
                                    status: "success",
                                });
                                break;
                            case 4:
                            case 5:
                                errors.push({
                                    description: "VOC sensor error",
                                    showCode: true,
                                    status: "warning",
                                });
                                break;
                            case 6:
                                errors.push({
                                    description: "Internal VOC sensor error",
                                    showCode: true,
                                    status: "warning",
                                });
                                break;
                        }
                        break;
                    case 3:
                        if(errorP == 1 && errorV == 0){
                            errors.push({
                                description: "CO2 sensor error",
                                status: "warning",
                            });
                        }
                        break;
                    case 4:
                        switch(errorP){
                            case 1:
                            case 2:
                                if(errorV == 0){
                                    errors.push({
                                        description: "PM sensor error",
                                        status: "warning",
                                    });
                                }
                                break;
                        }
                        break;
                }
                break;
            // Water meter sensor
            case 33:
                switch(errorT){
                    case 2:
                        errors.push({
                            description: "Water Encoder Error",
                            status: "warning",
                            showCode: true,
                        });
                        break;
                    case 3:
                        errors.push({
                            status: "success",
                        });
                        break;
                    case 4:
                        errors.push({
                            description: "No response from meter(s)",
                            status: "warning",
                            showCode: true,
                            causes: ["Make sure that a water encoder is connected to one of the Sensus ports on the bridge"],
                        });
                        break;
                }
                break;
            //Indoor Air Quality
            case 34:
                var error = this._getIaqError(errorT, errorP, errorV);
                if(error){
                  errors.push(error);
                }
                break;
            //Indoor Environmental Quality
            case 35:
                var error = this._getIeqError(errorT, errorP, errorV);
                if(error){
                  errors.push(error);
                }
                break;
        }

        //General errors for all connectors
        switch(errorT){
            case 204:
                errors.push({
                    description: "Error applying Update",
                    status: "warning",
                });
                break;
            case 205:
                errors.push({
                    description: "Error applying Action",
                    status: "warning",
                });
                break;
            case 230:
                errors.push({
                    description: "Action executed",
                    status: "success",
                    showCode: true,
                });
                break;
            case 231:
                errors.push({
                    description: "Update applied",
                    status: "success",
                    showCode: true,
                });
                break;
            case 233:
                errors.push({
                    status: "success",
                });
                break;
        }
        if (!this.options.moduleModel.showConnector(connector)){
            errors.push({
                description: "Sensor Disconnected",
                status: "danger",
                timestamp: this.options.moduleModel.getConnectorLastSeenText(connector),
                priority: 2500,
                //True if not otherwise specified
                inCombinedStatus: false
            });
        }
        return {
            errors: errors,
            errorT: errorT,
            errorP: errorP,
            errorV: errorV,
        };
    }
    _getIaqError(errorT, errorP, errorV){
        switch(errorT){
            case 1:
                return {
                    description: "T/RH Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be disconnected or damaged; contact Attune Support if error persists"],
                };
            case 2:
                return {
                    description: "VOC Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 3:
                return {
                    description: "CO2 Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be disconnected or damaged; contact Attune Support if error persists"],
                };
            case 4:
                return {
                    description: "Particulate Matter Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be disconnected or damaged; contact Attune Support if error persists"],
                };
            case 5:
                return {
                    description: "CO Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 6:
                return {
                    description: "O3 Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 7:
                return {
                    description: "SO2 Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 8:
                return {
                    description: "H2S Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 9:
                return {
                    description: "NO2 Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 10:
                return {
                    description: "HCHO Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be disconnected or damaged; contact Attune Support if error persists"],
                };
      }
    }
    _getIeqError(errorT, errorP, errorV){
        switch(errorT){
            case 2:
                return {
                    description: "Light Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 3:
                return {
                    description: "Motion Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be disconnected or damaged; contact Attune Support if error persists"],
                };
            case 4:
                return {
                    description: "Vibration Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 5:
                return {
                    description: "Pressure Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
            case 6:
                return {
                    description: "Sound Sensor Error",
                    status: "warning",
                    showCode: false,
                    causes: ["Sensor may be initializing; contact Attune Support if error persists"],
                };
        }
    }
    getSensorStatus(connector: number){
        var statuses = connector === 0 ? this._getInternalSensorStatus() :
            this._getAttachedSensorStatus(connector);

        if(_.size(statuses['errors']) == 0) statuses['errors'] = [];

        //If uncaught error, call it 'Check Module'
        //Else, send ignorable 'success' status
        if(!_.isEmpty(statuses) && _.size(statuses['errors']) == 0){
            if(statuses['errorT'] && statuses['errorT'] !== 0){
                if(connector == 0){
                    statuses['errors'].push({
                        status: 'warning',
                        shortDescription: 'Module Error',
                        description: 'Unknown module error',
                        showCode: true,
                    })
                } else {
                    statuses['errors'].push({
                        status: 'warning',
                        shortDescription: 'Sensor Error',
                        description: 'Unknown sensor error',
                        showCode: true,
                    })
                }
            }
        }

        //  We don't consider config pending messages in whether or not we display an "Unknown Error"
        // so we only check it afterwards

        //Only works for graph view if the sensorConfig is for module
        if(this.options.sensorConfigCollection
            && this.options.sensorConfigCollection.length
            && this.options.sensorConfigCollection.moduleId === this.options.moduleModel.id
            && !this.options.sensorConfigCollection.isFetching() // Prevent a "flicker" of config update pending before the collection was loaded
            && this.options.moduleModel.showConnector(connector)){ // Only check sensors that are connected

            if(this.hasHashChannel(connector) && !this.hashesMatch(connector)){
                statuses['errors'].push({
                        description: 'Configuration Update Pending',
                        shortDescription: 'Update Pending',
                        status: 'info',
                        inCombinedStatus: false,
                        channelId: connector === 0 ? this.options.moduleModel.getChannelId(0, 13) :
                            this.options.moduleModel.getChannelId(0, 10 + (connector*8))
                    });
            }
        }

        _.each(statuses['errors'], (status) => {
            if(!status['connector']) status['connector'] = connector;
            if(!status['priority']){
                status['priority'] = 0;
                // Success: 0, sensor warning: 100, sensor error: 200,
                // module error: 400, module warning: 300
                // If override message, normal priority + 1000
                if(connector == 0 && status['status'] != 'success' && status['status'] != 'info'){
                    status['priority'] += 200
                }
                if(status['status'] == 'danger'){
                    status['priority'] += 200
                } else if(status['status'] == 'warning'){
                    status['priority'] += 100
                }
                if(status['overrideMessage']){
                    status['priority'] += 1000
                }
            }
        });
        //Sort by error severity
        statuses['errors'] = _.reverse(_.sortBy(statuses['errors'], 'priority'));
        return statuses;
    }
    getConnectorsStatuses() {
        var statuses = [];
        //Create a list of statuses, indexed by connector index
        _.each([0, 1, 2, 3, 4], (j) => {
            var status = this.getSensorStatus(j);
            if(!_.isEmpty(status)) statuses.push(status);
        });
        return statuses;
    }
    getFlatStatuses(statuses?) {
        if(!statuses){
            statuses = this.getConnectorsStatuses();
        }
        return _.flatMap(statuses, 'errors');
    }

    getCombinedStatus(flatStatuses?, connector?) {
        if (connector != 0) { // For Bridges
            var combinedStatus = {
                status: 'success',
                graphDescription: 'Connected',
                description: 'Connected',
            };
        }else{
            var combinedStatus = { // For Nodes and Gateways (Internal Sensor)
                status: 'success',
                graphDescription: 'Online',
                description: 'Online',
            };
        }

        //If no statuses provided, calculate
        if(!flatStatuses){
            flatStatuses = this.getFlatStatuses();
        }
        //If sensor disconnected
        if(_.find(flatStatuses, {priority: 2500, connector: connector})){
            combinedStatus['status'] = 'faded';
            combinedStatus['graphDescription'] = 'Disconnected';
            combinedStatus['description'] = 'Disconnected';

        }
        //If forced into combinedStatus or otherwise high priority
        var importantStatuses = _.filter(flatStatuses, (status) => {
            return (status['inCombinedStatus'] || (status['priority'] > 0 && _.isUndefined(status['inCombinedStatus'])))
        });
        //If nothing important, return
        if(_.isEmpty(importantStatuses)){
            return combinedStatus;
        }
        //Find highest priority error
        var mostSevere = _.maxBy(importantStatuses, 'priority');

        if(mostSevere['status'] === 'danger') {
            var cautionType = 'Error';
        } else if (mostSevere['status'] === 'warning') {
            var cautionType = 'Warning';
        } else {
            var cautionType = "OK";
        }

        combinedStatus['status'] = mostSevere['status'];

        if(mostSevere['connector'] === 0 && connector === 0){
            combinedStatus['description'] = mostSevere['overrideMessage'] ? mostSevere['overrideMessage'] : cautionType;
            combinedStatus['graphDescription'] = mostSevere['shortDescription'] ? mostSevere['shortDescription'] : `Module ${cautionType}`;
        } else if (connector === 0) {
            combinedStatus['description'] = mostSevere['overrideMessage'] ? mostSevere['overrideMessage'] : `Sensor ${cautionType}`;
            combinedStatus['graphDescription'] = mostSevere['shortDescription'] ? mostSevere['shortDescription'] : `Sensor ${cautionType}`;
            if (mostSevere['status'] === 'danger'){
                combinedStatus['status'] = 'warning'
            }
        } else if (mostSevere['connector'] === connector) {
            combinedStatus['description'] = mostSevere['shortDescription'] ? mostSevere['shortDescription'] : `${cautionType}`;
        }
        if(mostSevere['status'] === 'info'){
            combinedStatus['graphStatus'] = 'success';
            combinedStatus['status'] = 'success';
            combinedStatus['graphDescription'] = 'OK';
        }

        combinedStatus['graphStatus'] = combinedStatus['graphStatus'] ? combinedStatus['graphStatus'] : combinedStatus['status'];

        return combinedStatus;
    }

    hasHashChannel(connector) {
        if(connector == 0){
            return this.options.moduleModel.getChannelValue(0, 13, null) !== null;
        } else  if (connector < 4) {
            return this.options.moduleModel.getChannelValue(0, 10 + 8*connector, null) !== null;
        }

        return false;
    }

    hashesMatch(connector){
        if(connector == 0){
            var hardwareHash = this.options.moduleModel.getChannelValue(0, 13, 0);
        } else  if (connector < 4) {
            var hardwareHash = this.options.moduleModel.getChannelValue(0, 10 + 8*connector, 0);
        } else {
            return true;
        }
        var serverHash = this._calcHash(connector);
        return serverHash === hardwareHash;

    }

    _calcHash(connector){
        var con = this.options.moduleModel.getConnector(connector);
        if(!this.options.sensorConfigCollection
            || _.size(this.options.sensorConfigCollection.models) == 0
            || !con ) return 0;

        var sensorId = con.sensor_id;

        var attrs   = {'high': [], 'low': []};
        var buffers = {'high': [], 'low': []};
        var hashes  = {'high':  0, 'low':  0};

        var configAttrs =  this.options.sensorConfigCollection.toJSON();

        var hashOrderZero = {'hash_order': 0, 'sensor_id': sensorId};
        var hashOrderOne = {'hash_order': 1, 'sensor_id': sensorId};

        attrs['high'] = _.sortBy(_.filter(configAttrs, <any> hashOrderOne), 'order');
        attrs['low']  = _.sortBy(_.filter(configAttrs, <any> hashOrderZero), 'order');

        _.each(buffers, (val, key) => {
            attrs[key] = _.filter(attrs[key], (attr) => {
                if(attr['connector'] !== 0 ) return true;

                if(this.options.moduleModel.isGateway()){
                    return (attr['type'].indexOf('NODE') !== 0);
                } else {
                    return (attr['type'].indexOf('GATEWAY') !== 0);
                }
            });
            _.each(attrs[key], (attr) => {
                buffers[key].push(
                    new BitArray(attr['value'], attr['num_bits'])
                )
            });
            buffers[key] = BitArray.concat(buffers[key]);
            hashes[key] = crc.crc16xmodem(buffers[key].toBuffer());
        });

        return ((hashes['high'] << 16) | hashes['low']) >>> 0;
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/common/SensorStatus.ts